import { useTheme } from "@mui/material";
import { VersionDistribution, VersionDistributionEntry } from "~/operations";
import { useGetImpactColorUpdated } from "~/components/impact/Updated/useGetImpactColorUpdated";

export type VersionDistrEntry = Pick<
  VersionDistributionEntry,
  "count" | "riskScoreRating" | "version"
>;

export type VersionDistr = Pick<VersionDistribution, "total"> & {
  distribution?: VersionDistrEntry[] | null;
};

export type DistrByColor = { [color: string]: VersionDistrEntry[] };

export type UseSoftwareVersionDistributionParams = {
  versionDistribution: VersionDistr | null | undefined;
};

export function useSoftwareVersionDistribution({
  versionDistribution,
}: UseSoftwareVersionDistributionParams) {
  const theme = useTheme();

  const total = versionDistribution?.total || 0;
  const distribution = versionDistribution?.distribution || [];

  const longestVersion = distribution.reduce((maxElement, current) => {
    return Math.max(maxElement, current.version.length);
  }, 0);

  const distributionByColor = distribution.reduce<DistrByColor>(
    (acc, current) => {
      const color = useGetImpactColorUpdated(current.riskScoreRating);
      const versionsByColor = acc[color] || [];
      versionsByColor.push(current);
      acc[color] = versionsByColor.sort((a, b) => b.count - a.count);
      return acc;
    },
    {},
  );

  const colorsOrder = [
    theme.palette.critical.main,
    theme.palette.high.main,
    theme.palette.medium.main,
    theme.palette.low.main,
    theme.palette.none.main,
  ];

  const sortedDistributionByColor = Object.entries(distributionByColor).sort(
    ([color1], [color2]) => {
      return colorsOrder.indexOf(color1) - colorsOrder.indexOf(color2);
    },
  );

  const threshold = 5;

  const getSortedDistributionByColorWithoutRest = (
    items: VersionDistrEntry[],
  ) => {
    return [...items].filter((version) => {
      return (version.count / total) * 100 > threshold;
    });
  };

  const getItemsToHide = (items: VersionDistrEntry[]) => {
    return (
      [...items].filter(
        (version) => (version.count / total) * 100 < threshold,
      ) || []
    );
  };

  const getRestItems = (items: VersionDistrEntry[]) => {
    return items.reduce((acc, item) => ({
      ...acc,
      count: acc.count + item.count,
    }));
  };

  const impactFractions = sortedDistributionByColor
    .reduce((acc, [color, items]) => {
      const totalGroupCount = items.reduce((agg, curr) => {
        return agg + curr.count;
      }, 0);

      return acc + `${totalGroupCount}fr `;
    }, "")
    .trim();

  return {
    sortedDistributionByColor,
    longestVersion,
    total,
    threshold,
    getSortedDistributionByColorWithoutRest,
    getItemsToHide,
    getRestItems,
    impactFractions,
  };
}
